// @flow
import React from 'react';
import { intlShape, FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import Checkbox from '../../common/checkbox/CheckboxComponent';
import TileMenu from '../../common/tile-menu/TileMenuComponent';
import ProductInfo from '../info/ProductInfoComponent';
import SizeLabel from '../../common/size-label/SizeLabelComponent';
import messages from '../../../data/products';
import DeleteDialog from '../../common/dialog/delete/DeleteDialogComponent';
import ImageLoader from '../../common/image-loader/ImageLoaderComponent';
import ProductImageCapsule from './ProductImageCapsuleComponent';

import './ProductImage.scss';

type Data = {
  id: string,
  lastUpdated: string,
  isDisabled?: boolean,
  isChecked?: boolean,
  isDeleted?: boolean,
  isDeleting?: boolean,
  availability: {
    isAvailable: boolean,
  },
  images: {
    thumb: string,
  },
  productSize: {
    name: string,
  },
  endpoints: {
    downloadPdfUrl: string,
    pixelEditUrl: string,
  },
  templateId: string,
};

type Props = {
  data: Data,
  permissions: Array<string>,
  disableCTAs: boolean,
  actions: {
    deleteRequest: Function,
    productSettings: Function,
    productChange: Function,
    closeOverlay: Function,
    showOverlay: Function,
  },
};

class ProductImageComponent extends React.Component {
  constructor() {
    super();
    this.state = {
      menuActive: false,
      menuShow: false,
    };
  }

  componentWillUnmount(): void {
    clearTimeout(this.timeout);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props): void {
    // Clear the hover / active status when we are in bulk selection mode (required for touch events)
    if (nextProps.disableCTAs === true && this.state.menuActive) {
      this.deactivateMenu();
    }
  }

  activateMenu = (): void => {
    this.setState({
      menuActive: true,
      menuShow: true,
    });
  };

  deactivateMenu = (): void => {
    this.setState({
      menuShow: false,
    });

    this.timeout = setTimeout(() => {
      if (!this.state.menuShow) {
        this.setState({
          menuActive: false,
        });
      }
    }, 800);
  };

  toggleMenu = (e: { preventDefault: () => void }): void => {
    e.preventDefault();
    if (this.state.menuShow) {
      this.deactivateMenu();
    } else {
      this.activateMenu();
    }
  };

  onSelect = (id: string): void => {
    this.props.actions.productChange(id, { isChecked: true });
  };

  onDeselect = (id: string): void => {
    this.props.actions.productChange(id, { isChecked: false });
    this.activateMenu();
  };

  onDelete = (): void => {
    this.props.actions.showOverlay({
      overlayName: DeleteDialog.displayName,
      overlayContent: this.deleteDialog(),
    });
  };

  deleteDialog(): Element {
    const checkedItemCount = 1;
    const deleteAction = () => {
      this.props.actions.productChange(this.props.data.id, { isDeleting: true });
      this.props.actions.deleteRequest({ ids: [this.props.data.id] });
    };

    return (
      <DeleteDialog
        description={
          <FormattedMessage
            {...messages.deleteDialogButtonDescription}
            values={{
              checkedItemCount,
              formattedCheckedItemCount: <strong>{checkedItemCount}</strong>,
            }}
          />
        }
        actions={{
          deleteAction,
          closeOverlay: this.props.actions.closeOverlay,
        }}
      />
    );
  }

  containerShowClass(): string {
    return this.state.menuShow || this.props.disableCTAs || this.props.data.isDeleting || this.props.data.isDeleted
      ? 'product-image-component__container--hovered'
      : '';
  }

  menuHideClass(): string {
    return ((!this.state.menuActive || this.props.disableCTAs) &&
      !(this.props.data.isDeleting || this.props.data.isDeleted)) ||
      this.props.data.isDisabled
      ? 'product-image-component__menu--hide'
      : '';
  }

  renderTileMenu(data: Data): Element {
    const containerShowClass = this.containerShowClass();
    const menuShowClass = this.menuHideClass();

    return (
      <div className={`product-image-component__container ${containerShowClass}`}>
        <span className={`product-image-component__menu ${menuShowClass}`}>
          <TileMenu
            productId={data.id}
            editLink={data.endpoints.pixelEditUrl}
            pdfLink={data.endpoints.downloadPdfUrl}
            permissions={this.props.permissions}
            onDelete={this.onDelete}
            isDeleting={data.isDeleting}
            labels={{
              edit: this.context.intl.formatMessage(messages.tileMenuEditTooltip),
              download: this.context.intl.formatMessage(messages.tileMenuDownloadTooltip),
              delete: this.context.intl.formatMessage(messages.tileMenuDeleteTooltip),
            }}
            templateId={data.templateId}
          />
        </span>
        {this.renderCheckbox()}
      </div>
    );
  }

  renderUnavailable(): Element {
    const className = classNames(
      'product-image-component__container--unavailable',
      'u-fontFamily-primaryMedium',
      this.state.menuShow && !this.props.disableCTAs ? 'product-image-component__container--unavailable--hovered' : '',
    );

    return (
      <div className={className}>
        <span className="unavailable__title">
          {this.context.intl.formatMessage(messages.tileMenuProductUnavailable)}
        </span>
        {this.renderCheckbox()}
      </div>
    );
  }

  renderImage(data: Data): Element {
    const { isAvailable } = data.availability;
    const imageStyle = isAvailable ? {} : { opacity: 0.5 };
    const replacementStyle = isAvailable ? {} : { opacity: 0 };

    return (
      <ImageLoader
        image={data.images.thumb}
        altText={data.productSize.name}
        imageStyle={imageStyle}
        replacementStyle={replacementStyle}
      />
    );
  }

  renderContent(data: Data) {
    if (data.availability.isAvailable) {
      return this.renderTileMenu(data);
    }
    return this.renderUnavailable();
  }

  renderCheckbox(): Element {
    const showCheckbox =
      this.props.hasBulkSelect &&
      !this.props.disableSelection &&
      (this.state.menuShow || (this.props.disableCTAs && !this.props.data.isDisabled));
    if (showCheckbox) {
      return (
        <span className="product-image-component__checkbox">
          <Checkbox
            value={this.props.data.id}
            isChecked={this.props.data.isChecked || false}
            onSelect={this.onSelect}
            onDeselect={this.onDeselect}
          />
        </span>
      );
    }

    return <div />;
  }

  renderImageCapsule(): ?Element {
    const { capsuleText, capsuleColour, tooltipText } = this.props;

    if (capsuleText) {
      return <ProductImageCapsule colour={capsuleColour} text={capsuleText} tooltipText={tooltipText} />;
    }

    return null;
  }

  render(): Element {
    const { data } = this.props;
    const checked = data.isChecked ? 'product-image-component--checked' : '';

    return (
      <div
        className={`product-image-component ${checked}`}
        onMouseEnter={this.activateMenu}
        onMouseLeave={this.deactivateMenu}
        onTouchEnd={this.toggleMenu}
      >
        {this.renderImage(data)}
        <span className="product-image-component__info">
          <ProductInfo lastUpdated={data.lastUpdated} />
        </span>
        {this.renderImageCapsule() || (
          <span className="product-image-component__label">
            <SizeLabel label={data.productSize.name} />
          </span>
        )}
        {this.renderContent(data)}
      </div>
    );
  }
}

ProductImageComponent.displayName = 'ProductImage';

ProductImageComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default ProductImageComponent;
